import { type Icon } from '@tabler/icons-react'

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { Link, useLocation } from 'react-router-dom'

type NavItem = {
  readonly title: string
  readonly url: string
  readonly icon?:
    | Icon
    | React.ComponentType<{ className?: string; strokeWidth?: number }>
  readonly badge?: number
}

interface NavMainProps {
  readonly items: readonly NavItem[]
}

export function NavMain({ items }: NavMainProps) {
  const location = useLocation()
  const { setOpenMobile } = useSidebar()

  const handleMenuItemClick = () => {
    // Close sidebar on mobile when menu item is clicked
    setOpenMobile(false)
  }

  return (
    <SidebarGroup>
      <SidebarGroupContent className="flex flex-col gap-2">
        <SidebarMenu className="gap-2">
          <nav aria-label="Main navigation">
            <ul className="gap-2">
              {items.map(item => {
                const Icon = item.icon

                // Helper function to check if current path should activate a nav item
                const isActiveForItem = (
                  itemUrl: string,
                  currentPath: string
                ): boolean => {
                  // Special case for dashboard root - only active on exact match
                  if (itemUrl === '/dashboard') {
                    return currentPath === '/dashboard'
                  }

                  // Special handling for sponsorship-related routes
                  if (itemUrl === '/dashboard/sponsorship-management') {
                    return (
                      currentPath.startsWith('/dashboard/sponsorship') ||
                      currentPath.startsWith('/dashboard/select-package')
                    )
                  }

                  // Default behavior - match if current path starts with item URL
                  return currentPath.startsWith(itemUrl)
                }

                const isActive = isActiveForItem(item.url, location.pathname)

                return (
                  <li key={item.title}>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild isActive={isActive}>
                        <Link
                          to={item.url}
                          className="font-medium relative"
                          onClick={handleMenuItemClick}
                          aria-current={isActive ? 'page' : undefined}
                          aria-label={`Navigate to ${item.title}${item.badge ? ` (${item.badge} unread)` : ''}`}
                          tabIndex={0}
                        >
                          {Icon && (
                            <Icon className="size-6" aria-hidden="true" />
                          )}
                          <span>{item.title}</span>
                          {item.badge !== undefined && item.badge > 0 && (
                            <span
                              className="ml-auto flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold text-white bg-ss-blue-500 rounded-full"
                              aria-label={`${item.badge} unread messages`}
                            >
                              {item.badge > 99 ? '99+' : item.badge}
                            </span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </li>
                )
              })}
            </ul>
          </nav>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
